import { BranchCode } from "api/risk";
import { CommercialIcon } from "components/icons/customIcons";
import { CardsSection } from "components/layout/Section/CardsSection";
import { StepPageWrapper } from "features/subscription-process/navigation/Steps/StepPageWrapper";
import { BranchStepConfigFactory, createStepConfig } from "features/subscription-process/navigation/Steps/createStepConfig";
import { useStepNavigation } from "features/subscription-process/navigation/Steps/useStepNavigation";
import { GenericLoading } from "components/ui/Loader";
import { RGIStepIdentifier } from "features/subscription-process/types/enums";
import { FC, useMemo } from "react";
import { ParcTenancyDashboard } from "./components/parc-tenancy/ParcTenancyDashboard";
import { useParcTenancyFile } from "./hooks/useParcTenancyFile";

interface ParcTenancyPageProps {
  riskId: string
}

/**
 * Étape « dossier location » du tunnel parc GLI : saisie des locataires bien par bien.
 * L'étape suivante (tarification) est bloquée tant que des biens couverts n'ont
 * aucun locataire valide — ils peuvent aussi être exclus de la couverture.
 */
export const ParcTenancyPage: FC<ParcTenancyPageProps> = ({ riskId }) => {
  const { file, isLoading, refetch } = useParcTenancyFile(riskId)

  const stepPageConfig: Partial<Record<BranchCode, BranchStepConfigFactory>> = useMemo(() => ({
    [BranchCode.RGI]: createStepConfig({
      next: {
        identifier: RGIStepIdentifier.Pricing,
        disabled: (file?.blockingCount ?? 1) > 0,
        route: "/simulation/tarification",
      },
      prev: {
        identifier: RGIStepIdentifier.RealEstateRegistration,
        route: "/simulation/inscription/gli/biens",
      },
    })
  }), [file]);

  const { stepConfig, goToNext, goToPrevious } = useStepNavigation(stepPageConfig)

  if (isLoading || !file) {
    return <GenericLoading isPageCentered loading />
  }

  return <div className="max-w-screen-2xl p-4 md:p-8 mt-8 mx-auto">
    <StepPageWrapper
      stepConfig={stepConfig}
      onNext={goToNext}
      onPrevious={goToPrevious}
      containerSize='full'
    >
      <CardsSection
        icon={<CommercialIcon className="h-8 w-8 text-primary" />}
        title="Informations locatives du parc"
      >
        <ParcTenancyDashboard riskId={riskId} file={file} refetch={refetch} />
      </CardsSection>
    </StepPageWrapper>
  </div>
}
